import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import './EmptyState.css';

export interface EmptyStateProps {
  /** Lucide icon (rendered ~28px). */
  icon?: ReactNode;
  /** One-line teach of what this surface holds. */
  title: ReactNode;
  /** Brief explanation of the value of filling it. Keep to one sentence. */
  description?: ReactNode;
  /** Primary action (usually a Button) that starts the obvious next step. */
  action?: ReactNode;
  /** `error` tone for failure states; `default` for genuinely empty. */
  tone?: 'default' | 'error';
  className?: string;
}

/**
 * Teaches the interface at a zero-data or failure moment. Never "nothing here":
 * acknowledge, explain the value, offer the next action. Use the `error` tone
 * for failed loads (with a retry action).
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'default',
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div className={cn('empty', `empty--${tone}`, className)} role="status">
      {icon && (
        <span className="empty__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <p className="empty__title">{title}</p>
      {description && <p className="empty__description">{description}</p>}
      {action && <div className="empty__action">{action}</div>}
    </div>
  );
}
